import BusyType from 'owa-service/lib/contract/BusyType';
import EventCardContent from './EventCardContent';
import React, { useContext, useEffect, useState } from 'react';
import { EVENT_CLICKED } from 'data/lib/mini-app-constants/commonEventSignalConstants';
import { EventCardProps } from '../schema/EventCard.types';
import { FreeBusyBar } from 'mini-freebusy/lib/components/FreeBusyBar';
import { FreeBusyConstants } from 'mini-freebusy';
import { generateColorScheme, getDefaultMiniColorScheme } from 'mini-color-utils/lib/colorUtils';
import { getDetailedCalendarEvent } from 'mini-calendar-event-services/lib/services/getDetailedCalendarEvent';
import { getEventCardClassNames, getEventCardStyles } from './EventCard.styles';
import { isSameDay, today } from 'owa-datetime';
import { ReactAppCtx } from 'mini-app-context';
import { ReactAppSourceContext } from 'data/lib/utils/ReactAppSourceContext';
import { useMoreEventDetailOperations } from '../hooks/useMoreEventDetailOperations';

const styles = require('./EventCard.scss');
const classNames = require('classnames/bind').bind(styles);

const DATA_TEST_ID = 'EventCard';

const EventCard = (props: EventCardProps): JSX.Element => {
    const {
        item,
        onClick,
        primaryColorForCustomColorScheme,
        eventCardAriaLabel,
        onRenderAdditionalContent,
        additionalStyles,
        timeColumnContent,
        isCharmSmall,
        colorScheme = props.primaryColorForCustomColorScheme
            ? generateColorScheme(primaryColorForCustomColorScheme)
            : getDefaultMiniColorScheme(),
    } = props;

    let [event, setEvent] = useState(item);
    useEffect(() => {
        async function fetchItemDetail() {
            const start = props?.item?.Start;

            if (!isSameDay(today(), start)) {
                return;
            }
            const event = await getDetailedCalendarEvent(
                props.item.ItemId,
                {
                    IncludeDeclinedMeetings: true,
                },
                null
            );

            setEvent(event);
        }

        fetchItemDetail();
    }, []);
    const { FreeBusyType, ResponseType, IsCancelled, IsEffectiveAllDayEvent } = item;

    // Cancelled event is considered as 'Free' event, so FreeBusyType is ignored here.
    const freeBusyValue = IsCancelled ? (FreeBusyConstants.Free as BusyType) : FreeBusyType;

    const ctx = useContext(ReactAppCtx);
    const actionSource = useContext(ReactAppSourceContext);
    const { viewEventDetails } = useMoreEventDetailOperations();
    const onEventClicked = (ev?: React.MouseEvent<any> | React.KeyboardEvent<any>) => {
        const { Id: itemId } = item?.ItemId ?? {};
        if (onClick) {
            onClick(itemId);
        } else {
            ctx?.signal(
                EVENT_CLICKED,
                {
                    isUIAction: true,
                    isMeeting: item && item.isEventAMeeting(),
                    eventId: itemId,
                },
                { actionSource: actionSource }
            );
            viewEventDetails(itemId);
        }
        ev?.stopPropagation();
    };

    /*
    - `event` contains full detail of the calendar event
      to be able to show JOIN meeting link on the event.
    - but `item` prop contains any updated value which
      is reflected from the store or ancestor
    - they need to be merged so that view doesn't reflect
      any stale value present in `event`
     */
    if (event && item) {
        for (const key of Object.keys(item)) {
            event[key] = item[key];
        }
    }

    const classes = getEventCardClassNames(getEventCardStyles, {
        className: null,
        dimPastEvents: false,
        freeBusyType: freeBusyValue,
        responseType: ResponseType,
        colorScheme: colorScheme,
        isCancelled: IsCancelled || ResponseType === 'Decline',
        isAllDay: IsEffectiveAllDayEvent,
    });

    return (
        <>
            <div
                role="button"
                id="itemCardWrapper"
                aria-label={eventCardAriaLabel}
                tabIndex={0}
                className={styles.itemCardWrapper}
                onClick={onEventClicked}
                onKeyUp={e => {
                    if (e.key === 'ArrowDown') {
                        document.getElementById('moduleSwitchFocusElement').focus();
                        return;
                    }

                    if (e.key === 'ArrowUp') {
                        document.getElementById('calendarHeaderFocusElement').focus();
                        return;
                    }

                    if (e.key !== 'Enter') {
                        return;
                    }
                    onEventClicked();
                }}
                data-focus-id={event.ItemId.Id}
                data-testid={DATA_TEST_ID}>
                {timeColumnContent && timeColumnContent()}
                {additionalStyles?.freeBusyBarStyles && (
                    <div
                        className={classNames(
                            styles.freeBusyBar,
                            additionalStyles?.freeBusyBarStyles
                        )}>
                        <FreeBusyBar
                            freeBusyType={freeBusyValue}
                            colorScheme={colorScheme}
                            iconStyles={additionalStyles?.freeBusyBarIconStyles}
                        />
                    </div>
                )}
                <div
                    className={classNames(
                        classes.content,
                        styles.itemContentWrapper,
                        additionalStyles?.contentStyles
                    )}>
                    <EventCardContent
                        item={event || item}
                        additionalStyles={additionalStyles}
                        isCharmSmall={isCharmSmall}
                    />
                    {onRenderAdditionalContent?.()}
                </div>
            </div>
        </>
    );
};

export default EventCard;
